import {getTimezoneByCountryCode, Timezones} from '@/utils/constants/timeZones'
import {format} from 'date-fns'
import {DateTime} from 'luxon'

const getJsDateFromObject = zoneDateTime => {
  const dateTime = zoneDateTime.toObject()

  if (!dateTime?.year || !dateTime?.month) return

  return new Date(dateTime.year, dateTime.month - 1, dateTime.day, dateTime.hour, dateTime.minute, dateTime.second)
}

/**
 * This function is used to format a UTC iso string to as human friendly text for specific country's timezone.
 */
export const formatPeriodDate = (date, country, formatStr = 'PPP') => {
  if (!date || !country) return ''

  const tz = Timezones[country]

  if (!tz) return undefined

  const zoneDateTime = DateTime.fromISO(date, {zone: Timezones.UTC}).setZone(tz)
  const dateAsObj = getJsDateFromObject(zoneDateTime)
  return dateAsObj ? format(dateAsObj, formatStr) : undefined
}

/**
 * This function is used to format a raw date string (YYYY-MM-DD or ISO) to MM/DD/YYYY format.
 */
export const formatDate = rawDate => {
  if (!rawDate) return '-'
  const pickerDate = rawDate.includes('T') ? splitUtcToLocalDate(rawDate) : rawDate
  const dateParts = pickerDate.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!dateParts || !DateTime.fromISO(pickerDate, {zone: 'utc'}).isValid) return '-'

  const [, year, month, day] = dateParts
  return `${month}/${day}/${year}`
}

/*
 * This function is used to convert a UTC iso string to a local date object from a specific timezone.
 */
export const convertUtcStringToJsDate = ({date, timezone}) => {
  if (!date || !timezone) return

  const zoneDateTime = DateTime.fromISO(date, {zone: Timezones.UTC}).setZone(timezone)
  return getJsDateFromObject(zoneDateTime)
}

/**
 * This function is used to convert a local JS Date object to a Luxon DateTime object in a specific timezone.
 */
export const convertJsDateToDateTimeByTimezone = ({date, timezone}) => {
  if (!date) return

  const dateLocale = date.toLocaleDateString('en-US')
  return DateTime.fromFormat(dateLocale, 'M/d/yyyy', {zone: timezone})
}

export const isStartDateLessThanCurrentDate = (startDate, timezone = getTimezoneByCountryCode()) => {
  if (!startDate) return false

  const todayInTimezone = DateTime.now().setZone(timezone).startOf('day')
  const parsedStartDate = DateTime.fromISO(startDate, {zone: timezone}).isValid
    ? DateTime.fromISO(startDate, {zone: timezone}).startOf('day')
    : DateTime.fromFormat(startDate, 'M/d/yyyy', {zone: timezone}).startOf('day')

  if (!parsedStartDate.isValid || !todayInTimezone.isValid) return false

  return parsedStartDate < todayInTimezone
}

/**
 * Check if an ISO date string is in the past (compared to today in the given timezone).
 * Returns true only if the entire date has passed (i.e., before today).
 * @param {string} isoDate - ISO date string to check
 * @param {string} timezone - Timezone to use for comparison (defaults to country timezone)
 * @returns {boolean} True if the date is before today, false otherwise
 */
export const isIsoDateInPast = (isoDate, timezone = getTimezoneByCountryCode()) => {
  if (!isoDate) return false

  const nowInTimezone = DateTime.now().setZone(timezone).startOf('day')
  const parsedDate = DateTime.fromISO(isoDate, {zone: 'utc'}).setZone(timezone).startOf('day')

  if (!parsedDate.isValid || !nowInTimezone.isValid) return false

  return parsedDate < nowInTimezone
}

/**
 * Combine a local date string (YYYY-MM-DD) and a time string (HH:mm or HH:mm:ss)
 * interpreted in the given timezone, and return a UTC ISO string.
 * Uses Luxon for timezone-aware conversion.
 */
export const combineLocalDateAndTimeToUtc = ({date, time, timezone = getTimezoneByCountryCode()}) => {
  if (!date || !time) return ''

  const [year, month, day] = date.split('-').map(Number)
  const timeParts = time.split(':').map(Number)
  const hour = timeParts[0] || 0
  const minute = timeParts[1] || 0
  const second = timeParts[2] || 0

  if (hour > 23 || minute > 59) return

  const dt = DateTime.fromObject({year, month, day, hour, minute, second}, {zone: timezone})

  return dt.toUTC().toISO({precision: 'second'})
}

/**
 * Get the current date in a specific timezone offset as an ISO string covering the whole day.
 * Returns the start of the day in the given timezone, e.g. 2026-06-16T00:00:00-05:00.
 */
export const getCurrentDateInTimezoneToUtc = (daysFromToday = 0, timezone = getTimezoneByCountryCode()) => {
  if (!timezone) return ''

  const base = DateTime.now().setZone(timezone)

  if (!base.isValid) return ''

  const nowInTimezone = base.plus({days: daysFromToday}).startOf('day')

  if (!nowInTimezone.isValid) return ''

  return nowInTimezone.toISO()
}

export const splitUtcToLocalTime = (isoString, timezone = getTimezoneByCountryCode()) => {
  if (!isoString) return '00:00'

  const dt = DateTime.fromISO(isoString, {zone: 'utc'}).setZone(timezone)

  return dt.toFormat('HH:mm')
}

export const splitUtcToLocalDate = (isoString, timezone = getTimezoneByCountryCode(), format = 'yyyy-MM-dd') => {
  if (!isoString) return ''

  const dt = DateTime.fromISO(isoString, {zone: 'utc'}).setZone(timezone)

  return dt.toFormat(format)
}

export const splitISOToLocalDate = isoString => {
  if (!isoString) return ''

  const formattedDate = new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  return formattedDate
}

export const buildTimestamp = (date, time, timezone = getTimezoneByCountryCode()) => {
  const [hours, minutes] = (time || '').split(':').map(Number)
  const cleanHours = isNaN(hours) ? 0 : hours
  const cleanMinutes = isNaN(minutes) ? 0 : minutes

  if (date instanceof Date) {
    return DateTime.fromJSDate(date, {zone: timezone})
      .set({hour: cleanHours, minute: cleanMinutes, second: 0, millisecond: 0})
      .toMillis()
  }

  if (typeof date !== 'string' || !date) return NaN

  const localDate = date.includes('T') ? splitUtcToLocalDate(date, timezone) : date
  const [year, month, day] = localDate.split('-').map(Number)

  if (!year || !month || !day) return NaN

  return DateTime.fromObject(
    {year, month, day, hour: cleanHours, minute: cleanMinutes, second: 0, millisecond: 0},
    {zone: timezone}
  ).toMillis()
}

export const isInvalidTime = time => {
  if (!time || time.length === 0) return false

  const digitsOnly = time.replace(/:/g, '')

  if (digitsOnly.length >= 2) {
    const hours = parseInt(digitsOnly.slice(0, 2), 10)
    if (hours > 23) return true
  }

  if (digitsOnly.length >= 4) {
    const minutes = parseInt(digitsOnly.slice(2, 4), 10)
    if (minutes > 59) return true
  }

  if (time.length >= 5) {
    return !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time)
  }

  return false
}
